import { useCallback, useRef } from 'react';
import type { Clinician } from '@features/organizations/types';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  selectHasActiveOrgFilters,
  selectIsOrgViewActive,
  selectSelectedPatientIds,
  selectSelectedProviderIds,
} from '../store/selectors';
import {
  resetOrgScheduleFilters,
  setOrgViewActive,
  setSelectedPatientIds,
  setSelectedProviderIds,
} from '../store/slice';

/**
 * Composable hook for org schedule filter state and actions.
 *
 * Wraps Redux selectors + dispatch so filter UI components
 * get a single, stable API without touching the store directly.
 */
export function useOrgScheduleFilters() {
  const dispatch = useAppDispatch();
  const initializedRef = useRef(false);

  // State
  const isOrgViewActive = useAppSelector(selectIsOrgViewActive);
  const selectedProviderIds = useAppSelector(selectSelectedProviderIds);
  const selectedPatientIds = useAppSelector(selectSelectedPatientIds);
  const hasActiveFilters = useAppSelector(selectHasActiveOrgFilters);

  // Actions
  const toggleOrgView = useCallback(
    (active: boolean) => {
      dispatch(setOrgViewActive(active));
      initializedRef.current = false;
    },
    [dispatch],
  );

  const updateSelectedProviders = useCallback(
    (ids: string[]) => {
      dispatch(setSelectedProviderIds(ids));
    },
    [dispatch],
  );

  const updateSelectedPatients = useCallback(
    (ids: string[]) => {
      dispatch(setSelectedPatientIds(ids));
    },
    [dispatch],
  );

  const clearAllFilters = useCallback(() => {
    dispatch(resetOrgScheduleFilters());
    initializedRef.current = false;
  }, [dispatch]);

  /**
   * Initialize provider selection from clinician list.
   * - null (never set) → select all clinicians
   * - string[] (persisted/user-set) → reconcile with current clinician list
   */
  const initializeProviders = useCallback(
    (clinicians: Clinician[]) => {
      if (initializedRef.current) return;
      initializedRef.current = true;

      const validIds = new Set(clinicians.map(c => c.userId));

      if (selectedProviderIds === null) {
        // First load: select all providers
        dispatch(setSelectedProviderIds(clinicians.map(c => c.userId)));
      } else {
        // Reconcile: remove IDs for clinicians that no longer exist
        const reconciled = selectedProviderIds.filter(id => validIds.has(id));
        if (reconciled.length !== selectedProviderIds.length) {
          dispatch(setSelectedProviderIds(reconciled));
        }
      }
    },
    [dispatch, selectedProviderIds],
  );

  return {
    isOrgViewActive,
    selectedProviderIds,
    selectedPatientIds,
    hasActiveFilters,
    toggleOrgView,
    updateSelectedProviders,
    updateSelectedPatients,
    clearAllFilters,
    initializeProviders,
  };
}
